import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {
  getCategoricalData,
  getCategoricalLegendItems,
} from 'app/components/Dashboard/utils/categoricalDataUtils';
import {
  MAX_FEATURES_FOR_MULTI_FEATURE_CHART,
  handleDownloadPng,
} from 'app/components/Dashboard/utils/chartUtils';
import {
  formatCategoricalAreaTableRows,
  handleDownloadCsv,
} from 'app/components/Dashboard/utils/tableUtils';
import {usePushNotification} from 'app/components/Notification';
import {ApiOrganization, getAreaUnit} from 'app/modules/Remote/Organization';
import {UNIT_AREA_ACRE} from 'app/utils/constants';
import {ImageLayerInfo} from 'app/utils/layers';

import {DashboardWidget} from '../DashboardWidget';
import {useMultiFeatureAnalysisData} from '../hooks/useMultiFeatureAnalysisData';
import {CategoricalStatResponse, MultifeatureAnalysisConfig} from '../types';
import {ChartDescription} from './ChartDescription';
import {MultiFeatureAnalysisCategoricalChart} from './MultiFeatureAnalysisCategoricalChart';
import {MultiFeatureAnalysisCategoricalTable} from './MultiFeatureAnalysisCategoricalTable';
import {MultiFeatureAnalysisSkeleton} from './MultiFeatureAnalysisSkeleton';
import {MFAWidgetControls} from './WidgetControls';

interface MultiFeatureCategoricalAnalysisWidgetProps {
  widgetConfig: MultifeatureAnalysisConfig;
  organization: I.ImmutableOf<ApiOrganization>;
}

export const MultiFeatureCategoricalAnalysisWidget: React.FC<
  MultiFeatureCategoricalAnalysisWidgetProps
> = ({widgetConfig, organization}) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const pushNotification = usePushNotification();

  const [isExpanded, setIsExpanded] = React.useState(false);
  const [hoveredFeatureId, setHoveredFeatureId] = React.useState<string | null>(null);
  const [displayFeatureIds, setDisplayFeatureIds] = React.useState<Set<string>>(new Set());

  // Categorical specific state
  const [currentCategoryId, setCurrentCategoryId] = React.useState<string>(
    widgetConfig.displayOptions?.categoricalKey || 'multiCategory'
  );
  const [dataViewUnit, setDataViewUnit] = React.useState<'area' | 'percentage'>(
    widgetConfig.statAggregation === 'area_percentages' ? 'percentage' : 'area'
  );
  const areaUnit = (organization && getAreaUnit(organization)) || UNIT_AREA_ACRE;

  const {data, isLoading, error, layer, allDates, noDataFound} = useMultiFeatureAnalysisData(
    organization.get('id'),
    widgetConfig
  );

  const {tableRows, categories} = React.useMemo(() => {
    if (!data || noDataFound) return {tableRows: [], categories: []};
    const {
      categoryChartData,
      singleCategoryChartDatas,
      categoryChartDataPercentages,
      singleCategoryChartDatasPercentages,
    } = getCategoricalData(
      data as CategoricalStatResponse,
      widgetConfig.layerKey,
      (layer as ImageLayerInfo).layerLegendMap || {},
      widgetConfig.timeAggregation,
      areaUnit,
      allDates
    );

    const selectedCategoryView = (() => {
      if (dataViewUnit === 'area') {
        return currentCategoryId === 'multiCategory'
          ? categoryChartData
          : singleCategoryChartDatas[currentCategoryId];
      }
      return currentCategoryId === 'multiCategory'
        ? categoryChartDataPercentages
        : singleCategoryChartDatasPercentages[currentCategoryId];
    })();

    // This might occur if a specific category is set in displayOptions.categoricalKey
    // but the option is configured incorrectly, like using the name of the category
    // instead of the id.
    if (!selectedCategoryView) return {tableRows: [], categories: []};

    return {
      tableRows: formatCategoricalAreaTableRows(selectedCategoryView, allDates),
      categories: Object.keys(singleCategoryChartDatas),
    };
  }, [
    data,
    noDataFound,
    widgetConfig.layerKey,
    layer,
    widgetConfig.timeAggregation,
    areaUnit,
    allDates,
    dataViewUnit,
    currentCategoryId,
  ]);

  const handleExportPng = React.useCallback(() => {
    if (!data) return;

    handleDownloadPng(
      chartRef.current?.querySelector('canvas'),
      widgetConfig,
      layer,
      isExpanded,
      () => {
        pushNotification({
          message: 'Error downloading chart',
          options: {
            intent: B.Intent.DANGER,
          },
        });
      },
      getCategoricalLegendItems({
        data: data as CategoricalStatResponse,
        displayFeatureIds,
        layerKey: widgetConfig.layerKey,
        statType: widgetConfig.statAggregation,
      })
    );
  }, [data, widgetConfig, layer, isExpanded, displayFeatureIds, pushNotification]);

  const exportTableAsCsv = React.useCallback(() => {
    handleDownloadCsv(tableRows, widgetConfig, () => {
      pushNotification({
        message: 'Error exporting table as CSV',
        options: {
          intent: B.Intent.DANGER,
        },
      });
    });
  }, [tableRows, widgetConfig, pushNotification]);

  if (isLoading) {
    return <MultiFeatureAnalysisSkeleton />;
  }

  return (
    <DashboardWidget
      title={widgetConfig.title}
      size={isExpanded ? 'large' : 'small'}
      controls={
        <MFAWidgetControls
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          onResize={(direction) => {
            if (direction === 'minimize') {
              // reset to initial state
              setCurrentCategoryId(widgetConfig.displayOptions?.categoricalKey || 'multiCategory');
              setDataViewUnit(
                widgetConfig.statAggregation === 'area_percentages' ? 'percentage' : 'area'
              );
            }
          }}
          onExportPng={handleExportPng}
          onExportCsv={exportTableAsCsv}
        />
      }
    >
      {error || !data || noDataFound ? (
        <B.NonIdealState
          icon="error"
          iconMuted={false}
          title="No data"
          description={
            noDataFound
              ? `No property statistics found for ${layer.display}`
              : (error?.message ?? 'Error visualizing data')
          }
        />
      ) : (
        <>
          <ChartDescription
            widgetConfig={widgetConfig}
            layer={layer}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
          />

          <div ref={chartRef}>
            <MultiFeatureAnalysisCategoricalChart
              featureStatsResponse={data as CategoricalStatResponse}
              widgetConfig={widgetConfig}
              isExpanded={isExpanded}
              alwaysShowLegend={displayFeatureIds.size > 0 || !currentCategoryId}
              hoveredFeatureId={hoveredFeatureId}
              currentCategoryId={currentCategoryId}
              dataViewUnit={dataViewUnit}
              areaUnit={areaUnit}
              displayFeatureIds={displayFeatureIds}
              allDates={allDates}
            />
          </div>

          {isExpanded && (
            <MultiFeatureAnalysisCategoricalTable
              rows={tableRows}
              widgetConfig={widgetConfig}
              displayFeatureIds={displayFeatureIds}
              setDisplayFeatureIds={setDisplayFeatureIds}
              setHoveredFeatureId={setHoveredFeatureId}
              allDates={allDates}
              currentCategoryId={currentCategoryId}
              setCurrentCategoryId={setCurrentCategoryId}
              dataViewUnit={dataViewUnit}
              setDataViewUnit={setDataViewUnit}
              organization={organization}
              hasManyFeatures={
                data && Object.keys(data).length > MAX_FEATURES_FOR_MULTI_FEATURE_CHART
              }
              categories={categories}
            />
          )}
        </>
      )}
    </DashboardWidget>
  );
};
